import { AddIcon, DeleteIcon } from "@chakra-ui/icons"
import {
	Box,
	Button,
	Checkbox,
	Flex,
	FormControl,
	IconButton,
	Input,
	Stack,
	Table,
	Tbody,
	Td,
	Text,
	Textarea,
	Th,
	Thead,
	Tr,
} from "@chakra-ui/react"
import { FieldArray, Formik } from "formik"
import moment from "moment"
import {
	ComponentProps,
	Dispatch,
	FC,
	MutableRefObject,
	SetStateAction,
	useEffect,
	useState,
} from "react"
import { EItemType } from "src/components/entries/EItemType"
import { PartyAddButton } from "src/components/shared/PartyAddButton"
import { ReactSelect, SelectOption } from "src/components/shared/ReactSelect"
import { DrawerForm } from "src/components/ui/DrawerForm"
import { InputDescription } from "src/components/ui/InputDescription"
import { InputLabel } from "src/components/ui/InputLabel"
import { EJobWorkItemType } from "src/domain/api/jobWorkItem/EJobWorkItemType"
import { IProductV1, ISubPartyV1 } from "src/domain/entities"
import { FormikOnSubmit } from "src/utils/types"
import { IJobWorkItemSlipBulkAddFormFields } from "./IJobWorkItemSlipBulkAddFormFields"

interface Props extends Omit<ComponentProps<typeof DrawerForm>, "children"> {
	type: EItemType
	subPartyList: ISubPartyV1[]
	productList: IProductV1[]
	isProductListLoading?: boolean
	defaultSelectedSubParty?: MutableRefObject<ISubPartyV1 | undefined>
	defaultSelectedProduct?: MutableRefObject<IProductV1 | undefined>
	defaultPrefix?: string
	prefillData?: Partial<IJobWorkItemSlipBulkAddFormFields>
	handleSubmit: FormikOnSubmit<IJobWorkItemSlipBulkAddFormFields>
	onProductAddSuccess?: Function
	onPartyAddSuccess?: Function
	fetchSlipNextNo: (
		setNextNo: Dispatch<SetStateAction<number>>,
		payload: { issueDate: string; prefix: string },
	) => void
	submissionProgress?: {
		isSubmitting: boolean
		successfulCount: number
		failedCount: number
		totalCount: number
	}
	setSubmissionProgress?: Dispatch<
		SetStateAction<{
			isSubmitting: boolean
			successfulCount: number
			failedCount: number
			totalCount: number
		}>
	>
}

export const JobWorkItemSlipBulkAddDrawerFormView: FC<Props> = ({
	type,
	subPartyList,
	defaultSelectedSubParty,
	defaultSelectedProduct,
	productList,
	isProductListLoading,
	defaultPrefix,
	handleSubmit,
	fetchSlipNextNo,
	onProductAddSuccess,
	onPartyAddSuccess,
	prefillData,
	submissionProgress,
	setSubmissionProgress,
	...rest
}) => {
	const defaultDate = moment().format("YYYY-MM-DD")

	const [issueDate, setIssueDate] = useState(defaultDate)
	const [prefix, setPrefix] = useState(defaultPrefix ?? "SL")
	const [nextSlipNo, setNextSlipNo] = useState<number>(1)

	let headerLabel =
		type === EItemType.INWARD ? "Add In Slip (Bulk)" : "Add Out Slip (Bulk)"

	const subPartyLabel =
		type === EItemType.INWARD ? "Select Supplier" : "Select Recipient"
	const subPartyDescription = `Party / Sub-Party ${
		type === EItemType.INWARD ? "from where goods came in" : "to whom goods going out"
	}`

	function getSubPartyLabel(subPartyId: string) {
		const subParty = subPartyList.find((subParty) => subParty.id === subPartyId)
		if (!subParty) return "unknown"

		let label = subParty.name
		if (!subParty.isDefault) label += ` (${subParty.party?.name})`
		return label
	}

	useEffect(() => {
		fetchSlipNextNo(setNextSlipNo, { issueDate, prefix })
	}, [fetchSlipNextNo, issueDate, prefix])

	const initialValues: IJobWorkItemSlipBulkAddFormFields = {
		itemType: type === EItemType.INWARD ? EJobWorkItemType.IN : EJobWorkItemType.OUT,
		subPartyId: defaultSelectedSubParty?.current?.id || "",
		issueDate,
		prefix,
		note: "",
		isLossInclusive: true,
		includeMarketRate: false,
		entries: [
			{
				productId: "",
				slipNo: nextSlipNo,
				grossQuantity: 0,
				bagQuantity: 0,
				workRate: 0,
				marketRate: 0,
				lossPercentage: 0,
			},
		],
		...prefillData,
	}

	return (
		<Formik<IJobWorkItemSlipBulkAddFormFields>
			initialValues={initialValues}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{({ values, isSubmitting, handleChange, setFieldValue }) => {
				const subPartyOptions = subPartyList.map((subParty) => ({
					label: getSubPartyLabel(subParty.id),
					value: subParty.id,
				}))

				const productOptions = productList.map((product) => ({
					label: product.name,
					value: product.id,
				}))

				// Helper function to check if a row is valid
				const isRowValid = (entry: any) => {
					return entry.productId && entry.grossQuantity > 0
				}

				// Check if form is valid for submission
				const isFormValid =
					values.subPartyId &&
					values.entries.length > 0 &&
					values.entries.every(isRowValid)

				return (
					<DrawerForm
						size="full"
						headerLabel={headerLabel}
						submitLabel="Save All Entries"
						isSubmitting={isSubmitting}
						isDisabled={!isFormValid}
						{...rest}
					>
						<Stack marginX={"auto"} spacing={4}>
							{/* Common Fields Section */}
							<Box borderWidth="1px" borderRadius="lg" p={4}>
								<Text fontWeight="bold" mb={3}>
									Common Details
								</Text>
								<Stack spacing={4}>
									<Flex gridGap={3}>
										{/* Issue date */}
										<FormControl flex={1}>
											<InputLabel label="Issue Date" />
											<Input
												name="issueDate"
												type="date"
												autoFocus
												value={values.issueDate}
												onChange={(e) => {
													setIssueDate(
														e.target.value ?? defaultDate,
													)
													setFieldValue(
														"issueDate",
														e.target.value ?? defaultDate,
													)
												}}
											/>
										</FormControl>
										{/* Sub Party */}
										<Flex flex={3} alignItems="center">
											<FormControl flex={1}>
												<InputLabel label={subPartyLabel} />
												<ReactSelect
													name="subPartyId"
													onChange={(newValue) => {
														const val = (
															newValue as SelectOption
														).value
														setFieldValue("subPartyId", val)

														if (defaultSelectedSubParty) {
															defaultSelectedSubParty.current =
																subPartyList.find(
																	(el) => el.id === val,
																)
														}
													}}
													options={subPartyOptions}
													isSearchable
													value={subPartyOptions.find(
														(el) =>
															el.value ===
															values.subPartyId,
													)}
												/>
												<InputDescription>
													{subPartyDescription}
												</InputDescription>
											</FormControl>
											<Box marginLeft={2}>
												<PartyAddButton
													onSuccess={async (party) => {
														await onPartyAddSuccess?.(party)
													}}
												/>
											</Box>
										</Flex>
										{/* Slip Prefix */}
										<FormControl flex={1}>
											<InputLabel label="Slip Prefix" />
											<Input
												name="prefix"
												maxLength={10}
												value={values.prefix}
												onChange={(e) => {
													setPrefix(e.target.value ?? "SL")
													setFieldValue(
														"prefix",
														e.target.value ?? "SL",
													)
												}}
											/>
										</FormControl>
									</Flex>

									<Flex gridGap={3}>
										{/* Note */}
										<FormControl flex={2}>
											<InputLabel label="Note" />
											<Textarea
												name="note"
												value={values.note}
												onChange={handleChange}
												placeholder="Optional note for all entries"
											/>
										</FormControl>
									</Flex>
								</Stack>
							</Box>

							{/* Product Entries Section */}
							<Box borderWidth="1px" borderRadius="lg" p={4}>
								<Flex
									justifyContent="space-between"
									alignItems="center"
									mb={3}
								>
									<Text fontWeight="bold">Product Entries</Text>
									<FieldArray name="entries">
										{({ push }) => (
											<Button
												leftIcon={<AddIcon />}
												colorScheme="blue"
												size="sm"
												onClick={() => {
													push({
														productId: "",
														slipNo:
															nextSlipNo +
															values.entries.length,
														grossQuantity: 0,
														bagQuantity: 0,
														workRate: 0,
														marketRate: 0,
														lossPercentage: 0,
													})
												}}
											>
												Add Row
											</Button>
										)}
									</FieldArray>
								</Flex>

								<FieldArray name="entries">
									{({ remove }) => (
										<Box>
											<Table variant="simple" size="sm">
												<Thead>
													<Tr>
														<Th>Product</Th>
														<Th>Slip No</Th>
														<Th>Gross Qty (KG)</Th>
														<Th>Bag Qty (KG)</Th>
														<Th>Work Rate</Th>
														<Th>Market Rate</Th>
														<Th>Loss %</Th>
														<Th>Action</Th>
													</Tr>
												</Thead>
												<Tbody>
													{values.entries.map(
														(entry, index) => (
															<Tr key={index}>
																<Td>
																	<Box width="200px">
																		<ReactSelect
																			name={`entries.${index}.productId`}
																			onChange={(
																				newValue,
																			) => {
																				const val =
																					(
																						newValue as SelectOption
																					)
																						.value
																				setFieldValue(
																					`entries.${index}.productId`,
																					val,
																				)
																			}}
																			value={productOptions.find(
																				(el) =>
																					el.value ===
																					entry.productId,
																			)}
																			isLoading={
																				isProductListLoading
																			}
																			options={
																				productOptions
																			}
																			isSearchable
																			placeholder="Select Product"
																		/>
																	</Box>
																</Td>
																<Td>
																	<Input
																		name={`entries.${index}.slipNo`}
																		value={
																			entry.slipNo
																		}
																		onChange={
																			handleChange
																		}
																		type="number"
																		size="sm"
																	/>
																</Td>
																<Td>
																	<Input
																		name={`entries.${index}.grossQuantity`}
																		value={
																			entry.grossQuantity
																		}
																		onChange={
																			handleChange
																		}
																		type="number"
																		size="sm"
																	/>
																</Td>
																<Td>
																	<Input
																		name={`entries.${index}.bagQuantity`}
																		value={
																			entry.bagQuantity
																		}
																		onChange={
																			handleChange
																		}
																		type="number"
																		size="sm"
																	/>
																</Td>
																<Td>
																	<Input
																		name={`entries.${index}.workRate`}
																		value={
																			entry.workRate
																		}
																		onChange={
																			handleChange
																		}
																		type="number"
																		size="sm"
																	/>
																</Td>
																<Td>
																	<Input
																		name={`entries.${index}.marketRate`}
																		value={
																			entry.marketRate
																		}
																		onChange={
																			handleChange
																		}
																		type="number"
																		size="sm"
																	/>
																</Td>
																<Td>
																	<Input
																		name={`entries.${index}.lossPercentage`}
																		value={
																			entry.lossPercentage
																		}
																		onChange={
																			handleChange
																		}
																		type="number"
																		size="sm"
																	/>
																</Td>
																<Td>
																	<IconButton
																		icon={
																			<DeleteIcon />
																		}
																		aria-label="Remove row"
																		size="sm"
																		colorScheme="red"
																		variant="ghost"
																		onClick={() =>
																			remove(index)
																		}
																		isDisabled={
																			values.entries
																				.length ===
																			1
																		}
																	/>
																</Td>
															</Tr>
														),
													)}
												</Tbody>
											</Table>
										</Box>
									)}
								</FieldArray>
							</Box>

							{/* Additional Options */}
							<Box borderWidth="1px" borderRadius="lg" p={4}>
								<Text fontWeight="bold" mb={3}>
									Additional Options
								</Text>
								<Stack spacing={3}>
									<Checkbox
										name="isLossInclusive"
										isChecked={values.isLossInclusive}
										onChange={(e) =>
											setFieldValue(
												"isLossInclusive",
												e.target.checked,
											)
										}
									>
										Loss Inclusive
									</Checkbox>
									<Checkbox
										name="includeMarketRate"
										isChecked={values.includeMarketRate}
										onChange={(e) =>
											setFieldValue(
												"includeMarketRate",
												e.target.checked,
											)
										}
									>
										Include Market Rate
									</Checkbox>
								</Stack>
							</Box>

							{/* Submission Progress */}
							{submissionProgress?.isSubmitting && (
								<Box
									borderWidth="1px"
									borderRadius="lg"
									p={4}
									bg="blue.50"
								>
									<Text fontWeight="bold" mb={2}>
										Submission Progress
									</Text>
									<Stack spacing={2}>
										<Text fontSize="sm">
											Successfully submitted:{" "}
											{submissionProgress.successfulCount} /{" "}
											{submissionProgress.totalCount}
										</Text>
										{submissionProgress.failedCount > 0 && (
											<Text fontSize="sm" color="red.500">
												Failed: {submissionProgress.failedCount}
											</Text>
										)}
										<Text fontSize="sm" color="gray.600">
											Remaining entries will be retried
											automatically.
										</Text>
									</Stack>
								</Box>
							)}
						</Stack>
					</DrawerForm>
				)
			}}
		</Formik>
	)
}
